/**
 * Carte : création, câblage et synchronisation avec l'état.
 *
 * La carte ne décide rien. Elle traduit les gestes en mutations d'état
 * (select, clearSelection) et redessine quand l'état change. Un clic local et
 * une modification arrivée par le temps réel empruntent donc exactement le
 * même chemin.
 */

import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

import { toFeatureCollection } from '../data/establishments.js'
import { departmentsOf, hospitalStatus } from '../domain/entities.js'
import { isVisible } from '../domain/filters.js'
import { hasData, recordKey } from '../domain/record.js'
import { clearSelection, getNotes, getState, select, subscribe } from '../state.js'
import { escapeHtml } from '../ui/dom.js'
import { createHover } from './hover.js'
import { addShapeImages } from './icons.js'
import {
  HOVER_LAYERS,
  MARKER_LAYERS,
  SELECTION_LAYERS,
  addMarkerLayers,
  applyStatusFilter,
  applyTypeVisibility,
} from './layers.js'
import { ATTRIBUTION, INITIAL_VIEW, baseStyle } from './style.js'

const EMPTY = { type: 'FeatureCollection', features: [] }

const SHAPE_BY_LAYER = { 'gmf-dots': 'c', 'gmfu-tri': 't', 'hosp-sq': 's' }

export function createMap(container) {
  const state = getState()
  const gmfList = state.establishments.filter((e) => e.kind === 'gmf')
  const hospList = state.establishments.filter((e) => e.kind === 'hosp')

  /**
   * Le statut à peindre. Un hôpital n'a pas de statut propre : c'est celui de
   * sa relation la plus avancée, dérivé de ses départements.
   */
  const statusFor = (est) =>
    est.kind === 'hosp'
      ? hospitalStatus(est.id, state.records)
      : state.records.get(recordKey(est.kind, est.id))?.status || 'prospect'

  /**
   * Le cerne « documenté ». Pour un hôpital, il vient de ses départements :
   * l'hôpital lui-même n'a ni effectif ni notes.
   */
  const annotated = (est) => {
    if (est.kind === 'hosp') {
      return departmentsOf(est.id, state.records).some(
        (d) => hasData(d, getNotes('dept', d.id).length),
      )
    }
    return hasData(state.records.get(recordKey(est.kind, est.id)), getNotes(est.kind, est.id).length)
  }

  const map = new maplibregl.Map({
    container,
    style: baseStyle({
      gmf: { type: 'geojson', data: EMPTY, promoteId: 'id' },
      hosp: { type: 'geojson', data: EMPTY, promoteId: 'id' },
      sel: { type: 'geojson', data: EMPTY },
      hov: { type: 'geojson', data: EMPTY },
    }),
    center: INITIAL_VIEW.center,
    zoom: INITIAL_VIEW.zoom,
    minZoom: INITIAL_VIEW.minZoom,
    maxZoom: INITIAL_VIEW.maxZoom,
    attributionControl: false,
    // La rotation n'apporte rien sur une carte de prospection, et un
    // deux-doigts maladroit sur trackpad fait basculer le nord sans le vouloir.
    dragRotate: false,
  })

  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
  map.addControl(
    new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserLocation: true,
    }),
    'top-right',
  )
  map.addControl(new maplibregl.ScaleControl({ maxWidth: 96, unit: 'metric' }), 'bottom-right')
  map.addControl(
    new maplibregl.AttributionControl({ compact: true, customAttribution: ATTRIBUTION }),
    'bottom-right',
  )

  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 14,
    anchor: 'bottom',
    className: 'hovertip',
    maxWidth: '230px',
  })

  const hover = createHover(map, popup)

  /** Le marqueur sous le curseur, ou null. */
  function hitTest(point) {
    const [feature] = map.queryRenderedFeatures(point, { layers: MARKER_LAYERS })
    if (!feature) return null
    const layer = feature.layer.id
    return {
      kind: layer === 'hosp-sq' ? 'hosp' : 'gmf',
      id: feature.id,
      shape: SHAPE_BY_LAYER[layer],
      coords: feature.geometry.coordinates,
      status: feature.properties.status,
      labelHtml: escapeHtml(feature.properties.nom),
    }
  }

  function refreshSources() {
    map.getSource('gmf')?.setData(toFeatureCollection(gmfList, statusFor, annotated))
    map.getSource('hosp')?.setData(toFeatureCollection(hospList, statusFor, annotated))
  }

  /**
   * Le marqueur sélectionné, recopié agrandi dans la source `sel`.
   *
   * Il porte les mêmes propriétés que le calque de survol — forme, statut,
   * image — puisque les couches sel-* et hov-* rendent la même chose à la même
   * taille. La sélection est simplement un survol qui persiste.
   */
  function refreshSelection() {
    const source = map.getSource('sel')
    if (!source) return

    const est = state.selection
      ? state.establishments.find(
          (e) => e.kind === state.selection.kind && e.id === state.selection.id,
        )
      : null

    // Un marqueur que les filtres masquent ne doit pas réapparaître en grand
    // sous prétexte qu'il est sélectionné : ce serait contredire le filtre à
    // l'écran. Le panneau de détail, lui, reste ouvert.
    if (!est || !isVisible(est, state.records, state.statusFilters, state.typeFilters)) {
      source.setData(EMPTY)
      return
    }

    const shape = est.kind === 'hosp' ? 's' : est.type === 'GMF-U' ? 't' : 'c'
    const status = statusFor(est)

    source.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [est.lon, est.lat] },
        properties: {
          shape,
          status,
          image: `${shape === 't' ? 'tri' : 'sq'}-${status}`,
        },
      }],
    })
  }

  const isSelected = (target) =>
    Boolean(state.selection && state.selection.kind === target.kind && state.selection.id === target.id)

  map.on('load', () => {
    // Le curseur par défaut de MapLibre est une main ; Google Maps a habitué
    // tout le monde à la flèche, la main n'apparaissant que pendant le glissé.
    map.getCanvas().style.cursor = ''

    addShapeImages(map)
    addMarkerLayers(map)
    refreshSources()
    applyStatusFilter(map, state.statusFilters)
    applyTypeVisibility(map, state.typeFilters)

    map.on('mousemove', (e) => {
      // Le calque de survol recouvre le marqueur d'origine : sans ce test, il
      // masquerait sa propre cible et le survol clignoterait.
      if (hover.current && map.queryRenderedFeatures(e.point, { layers: HOVER_LAYERS }).length) return

      const target = hitTest(e.point)

      // Le marqueur sélectionné est déjà à la taille haute. Y lancer
      // l'animation de survol ferait grossir une copie depuis rien, à
      // l'intérieur de lui-même. Et son nom est dans le panneau : l'infobulle
      // n'apprendrait rien.
      if (target && isSelected(target)) {
        if (hover.current) hover.leave()
        return
      }

      if (target) hover.enter(target)
      else if (hover.current) hover.leave()
    })

    map.on('click', (e) => {
      let target = hitTest(e.point)

      // Le marqueur grossi peut déborder de sa propre zone : un clic sur le
      // débord doit valoir clic sur le marqueur.
      if (!target && hover.current && map.queryRenderedFeatures(e.point, { layers: HOVER_LAYERS }).length) {
        target = hover.current
      }

      // Même débord pour le marqueur sélectionné. Sans ce garde, cliquer sur
      // ce qu'on voit très bien à l'écran le désélectionnerait.
      if (!target && state.selection
          && map.queryRenderedFeatures(e.point, { layers: SELECTION_LAYERS }).length) {
        return
      }

      popup.remove()
      if (target) select(target.kind, target.id)
      else clearSelection()
    })

    map.on('zoom', () => hover.onZoom())

    // La carte occupe une colonne de grille : ouvrir la colonne de résultats
    // la rétrécit sans que la fenêtre bouge, donc sans événement `resize`.
    new ResizeObserver(() => map.resize()).observe(map.getContainer().parentElement)
  })

  subscribe((_s, changed) => {
    if (!map.isStyleLoaded()) return
    if (changed.includes('records') || changed.includes('notes')) refreshSources()
    if (changed.includes('statusFilters')) applyStatusFilter(map, state.statusFilters)
    if (changed.includes('typeFilters')) applyTypeVisibility(map, state.typeFilters)

    // La copie sélectionnée dépend aussi du statut (sa couleur) et des filtres
    // (sa présence) — pas seulement de la sélection elle-même. Sans `records`
    // ici, changer le statut de la fiche ouverte laisserait le gros marqueur
    // à son ancienne couleur.
    if (['selection', 'records', 'statusFilters', 'typeFilters'].some((c) => changed.includes(c))) {
      refreshSelection()
    }
  })

  return {
    map,
    flyTo(est, zoom = 13) {
      map.flyTo({ center: [est.lon, est.lat], zoom, speed: 1.2 })
    },
    destroy() {
      hover.destroy()
      map.remove()
    },
  }
}
